// i18n: one JSON file per language in ./locales (en, ru, uz, tr). To add a language: add its code to LANGUAGES in
// packages/shared/src/constants.ts and drop <code>.json here (copy en.json and translate the values).
// Missing keys fall back to English. Each person chooses their own language (profile); the company setting is the default.
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { LANGUAGES as LANGS } from '@vegapart/shared';
import en from './locales/en.json';

export const LANGUAGES: Record<string, string> = Object.fromEntries(LANGS.map(l => [l.code, l.name]));

// Every locales/*.json except English is loaded only when chosen (smaller first download)
const files = import.meta.glob<{ default: Record<string, string> }>(['./locales/*.json', '!./locales/en.json']);
const loaderFor = (lang: string) => files[`./locales/${lang}.json`];

const STORE = 'vp_lang';
export function savedLanguage(): string | null {
  try { return localStorage.getItem(STORE); } catch { return null; }
}
/** Language before sign-in: last used on this device, else the browser's, else English */
export function guessLanguage(): string {
  // Links from the vegapart.com website carry the visitor's language (?lang=uz)
  const fromLink = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('lang') : null;
  if (fromLink && LANGUAGES[fromLink]) return fromLink;
  const saved = savedLanguage();
  if (saved && LANGUAGES[saved]) return saved;
  const nav = (typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : []).map(l => l.slice(0, 2).toLowerCase());
  return nav.find(l => LANGUAGES[l]) ?? 'en';
}

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng: 'en',
  fallbackLng: 'en',
  keySeparator: false,          // keys are flat ("wo.new"), not nested
  nsSeparator: false,
  interpolation: { escapeValue: false }
});

export async function setLanguage(lang: string) {
  if (!LANGUAGES[lang]) lang = 'en';
  const load = loaderFor(lang);
  if (lang !== 'en' && !i18n.hasResourceBundle(lang, 'translation') && load) {
    const mod = await load();
    i18n.addResourceBundle(lang, 'translation', mod.default);
  }
  await i18n.changeLanguage(lang);
  document.documentElement.lang = lang;
  try { localStorage.setItem(STORE, lang); } catch { /* private mode */ }
}

export default i18n;
